import React, { useState, useEffect } from 'react';
import { Star, Plus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Testimonials = () => {
  const { language } = useLanguage();
  const { toast } = useToast();

  const [testimonials, setTestimonials] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    text: '',
    rating: 5
  });

  // =============================
  // FETCH REVIEWS
  // =============================
  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/testimonials?approved_only=true`
      );

      const approvedReviews = response.data || [];
      setTestimonials(approvedReviews);

      if (approvedReviews.length > 0) {
        const total = approvedReviews.reduce(
          (sum, review) => sum + review.rating,
          0
        );
        const avg = total / approvedReviews.length;
        setAverageRating(parseFloat(avg.toFixed(1)));
      } else {
        setAverageRating(0);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // =============================
  // FORM HANDLING
  // =============================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/testimonials`, {
        ...formData,
        rating: Number(formData.rating)
      });

      toast({
        title: language === 'en' ? "Thank you!" : "¡Gracias!",
        description: language === 'en'
          ? "Your review has been submitted and will appear after approval."
          : "Tu reseña ha sido enviada y aparecerá después de su aprobación.",
      });

      setFormData({
        name: '',
        location: '',
        text: '',
        rating: 5
      });

      setShowForm(false);
      fetchTestimonials();
    } catch (error) {
      toast({
        title: "Error",
        description: language === 'en'
          ? "Failed to submit review."
          : "Error al enviar la reseña.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // STAR RENDERS
  // =============================
  const renderStars = (rating) => {
    return Array.from({ length: rating }).map((_, index) => (
      <Star key={index} className="h-5 w-5 fill-amber-400 text-amber-400" />
    ));
  };

  const renderAverageStars = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`h-6 w-6 ${
          index < Math.round(averageRating)
            ? "fill-amber-400 text-amber-400"
            : "text-slate-300"
        }`}
      />
    ));
  };

  const renderSelectableStars = () => {
    return Array.from({ length: 5 }).map((_, index) => {
      const starValue = index + 1;
      return (
        <Star
          key={starValue}
          onClick={() =>
            setFormData({ ...formData, rating: starValue })
          }
          className={`h-6 w-6 cursor-pointer transition ${
            starValue <= formData.rating
              ? "fill-amber-400 text-amber-400"
              : "text-slate-300 hover:text-amber-300"
          }`}
        />
      );
    });
  };

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">

        {/* HEADER */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            {language === 'en' ? "Client Reviews" : "Reseñas de Clientes"}
          </h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto"></div>
        </div>

        {/* REVIEWS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card key={testimonial.id || index} className="shadow-lg">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {renderStars(testimonial.rating)}
                </div>

                <p className="italic mb-6">
                  "{testimonial.text}"
                </p>

                <div className="border-t pt-4">
                  <p className="font-bold">{testimonial.name}</p>
                  <p className="text-sm text-slate-600">
                    {testimonial.role}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* AVERAGE SECTION */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-6 bg-amber-50 px-10 py-6 rounded-2xl shadow-md">
            <div className="flex items-center space-x-3">
              <div className="flex">
                {renderAverageStars()}
              </div>
              <span className="text-3xl font-bold text-slate-900">
                {averageRating}
              </span>
            </div>
            <div className="w-px h-10 bg-slate-300"></div>
            <div>
              <p className="text-slate-600 font-medium">
                {language === 'en'
                  ? `Based on ${testimonials.length} reviews`
                  : `Basado en ${testimonials.length} reseñas`}
              </p>
            </div>
          </div>
        </div>

        {/* ADD REVIEW BUTTON */}
        <div className="mt-16 text-center">
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3"
          >
            <Plus className="mr-2 h-5 w-5" />
            {language === 'en' ? "Write a Review" : "Escribir Reseña"}
          </Button>
        </div>

        {/* REVIEW FORM */}
        {showForm && (
          <div className="mt-10 max-w-2xl mx-auto bg-slate-50 p-8 rounded-2xl shadow-md">
            <form onSubmit={handleSubmit} className="space-y-6">

              <div>
                <Label>Name</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label>Location</Label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <Label>Rating</Label>
                <div className="flex mt-2">
                  {renderSelectableStars()}
                </div>
              </div>

              <div>
                <Label>Review</Label>
                <Textarea
                  name="text"
                  value={formData.text}
                  onChange={handleChange}
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                {loading
                  ? language === 'en' ? "Submitting..." : "Enviando..."
                  : language === 'en' ? "Submit Review" : "Enviar Reseña"}
              </Button>

            </form>
          </div>
        )}

      </div>
    </section>
  );
};

export default Testimonials;